import { useMutation, useQuery } from '@tanstack/react-query'
import { PagesFilterParams, CreatePageParams, UpdatePageParams } from '@/types/page'
import { SortOrderEnum } from '@/enums'
import { usePagesStore } from '@/store'
import { useBulkDeleteSuccessHandler } from './use-bulk-delete'

export const usePages = (params: PagesFilterParams) => {
  const fetchPages = usePagesStore(state => state.fetchPages)

  return useQuery({
    queryKey: ['pages', params],
    queryFn: () => fetchPages(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  })
}

export const usePage = (id: string) => {
  const getPage = usePagesStore(state => state.getPage)

  return useQuery({
    queryKey: ['page', id],
    queryFn: () => getPage(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!id
  })
}

export const useCreatePage = () => {
  const createPage = usePagesStore(state => state.createPage)

  return useMutation({
    mutationFn: (params: CreatePageParams) => createPage(params),
    onSuccess: () => {}
  })
}

export const useUpdatePage = () => {
  const updatePage = usePagesStore(state => state.updatePage)

  return useMutation({
    mutationFn: ({ id, params }: { id: string; params: UpdatePageParams }) => updatePage(id, params),
    onSuccess: () => {}
  })
}

export const useDeletePage = () => {
  const deletePage = usePagesStore(state => state.deletePage)

  return useMutation({
    mutationFn: (id: string) => deletePage(id),
    onSuccess: () => {}
  })
}

export const useBulkDeletePages = () => {
  const bulkDeletePages = usePagesStore(state => state.bulkDeletePages)
  const deselectAllPages = usePagesStore(state => state.deselectAllPages)
  const handleBulkDeleteSuccess = useBulkDeleteSuccessHandler()

  return useMutation({
    mutationFn: (ids: string[]) => bulkDeletePages(ids),
    onSuccess: response => {
      deselectAllPages()
      handleBulkDeleteSuccess(response)
    }
  })
}

export const useSortPage = () => {
  const sortPage = usePagesStore(state => state.sortPage)

  return useMutation({
    mutationFn: ({ id, type }: { id: string; type: SortOrderEnum }) => sortPage(id, type),
    onSuccess: () => {}
  })
}
